/*
 * Who a horizontal stroke on a page belongs to (2026-10-25,
 * docs/UI_HUBS_AND_GESTURES_PLAN.md sections 2-3): the page's swipe between
 * hubs (Home -> Bank -> Experiments), an isolate card's own swipe (its Lab
 * analytics tray), or nobody.
 *
 * Asked where the finger lands, before anything moves, in this order:
 *
 *   1. what takes a drag for itself -- a field, a slider, a canvas, a map,
 *      anything marked data-gesture="off", the Report issue tab, a dialog or
 *      a sheet -- keeps it: nobody;
 *   2. the outer EDGE_GUARD_PX of either side is the system's: Android's
 *      back gesture and iOS Safari's back and forward start there, and a web
 *      page cannot claim them (the Report issue tab sits there too): nobody;
 *   3. anything that scrolls sideways under the finger scrolls: nobody;
 *   4. a stacked isolate card (data-swipe="card") is the card's;
 *   5. anything else is the page's.
 *
 * Pure but for reading the DOM it is given, so it is tested on built trees.
 */

// The system's share of each side, px.
export const EDGE_GUARD_PX = 24;

const OWN_DRAG = [
  'input', 'textarea', 'select', '[contenteditable="true"]', '[role="slider"]', 'canvas',
  '.leaflet-container', '[data-gesture="off"]', '[data-report-issue-tab]', '[role="dialog"]',
].join(', ');

/** Whether `el` scrolls sideways (its content is wider and it lets it scroll). */
function scrollsSideways(el, view) {
  if (!(el.scrollWidth > el.clientWidth)) return false;
  const overflow = (view?.getComputedStyle?.(el)?.overflowX || '').toLowerCase();
  return overflow === 'auto' || overflow === 'scroll';
}

/**
 * 'page', 'card' or 'none' for a stroke starting on `target` at viewport x
 * `x`, in a viewport `width` px wide. `root` bounds the walk up the tree (the
 * element the page listens on); `edgeGuardPx` may be set for a test.
 */
export function swipeOwner(target, { x, width, root = null, edgeGuardPx = EDGE_GUARD_PX } = {}) {
  if (!target?.closest) return 'none';
  if (target.closest(OWN_DRAG)) return 'none';
  if (!(x >= edgeGuardPx) || !(x <= width - edgeGuardPx)) return 'none';
  const view = target.ownerDocument?.defaultView;
  for (let el = target; el && el !== root?.parentElement; el = el.parentElement) {
    if (scrollsSideways(el, view)) return 'none';
    if (el === root) break;
  }
  if (target.closest('[data-swipe="card"]')) return 'card';
  return 'page';
}
